<form [formGroup]="form" (ngSubmit)="onSubmit()" class="max-w-3xl mx-auto py-6 px-6 space-y-8">

  <!-- ── General ─────────────────────────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      General
    </h2>

    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-sm text-gray-700 dark:text-gray-300">Formulario multi-paso</span>
      <input type="checkbox" formControlName="isMultiStep" class="w-4 h-4 cursor-pointer accent-brand-500">
    </label>

    @if (form.value.isMultiStep) {
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Total de pasos</label>
        <input type="number" formControlName="totalSteps" min="1" (input)="onTotalStepsChange()" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
    }

    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-sm text-gray-700 dark:text-gray-300">Ocultar marca del proveedor</span>
      <input type="checkbox" formControlName="hideProviderBranding" class="w-4 h-4 cursor-pointer accent-brand-500">
    </label>
  </section>

  <!-- ── Pasos del formulario (inc22) ────────────────────────────────────── -->
  @if (form.value.isMultiStep) {
    <section class="space-y-4">
      <div class="border-b border-gray-200 dark:border-gray-800 pb-2">
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Pasos del formulario</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Título, descripción e imagen que se muestran en cada paso. Asigna los campos a cada paso desde la pestaña <strong>Campos</strong>.</p>
      </div>

      @for (step of steps; track step.step) {
        <div class="rounded-xl border border-gray-200 dark:border-gray-800 p-4 space-y-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-50 dark:bg-brand-500/15 text-brand-600 dark:text-brand-400 text-xs font-semibold">{{ step.step }}</span>
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Paso {{ step.step }}</span>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Título del paso</label>
            <input type="text" [(ngModel)]="step.title" [ngModelOptions]="{ standalone: true }" placeholder="Ej. Datos personales" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Descripción del paso</label>
            <textarea [(ngModel)]="step.description" [ngModelOptions]="{ standalone: true }" rows="2" placeholder="Texto de ayuda para este paso" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"></textarea>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">URL de imagen (opcional)</label>
            <input type="url" [(ngModel)]="step.imageUrl" [ngModelOptions]="{ standalone: true }" placeholder="https://..." class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
        </div>
      }
    </section>

    <!-- ── Saltos condicionales entre pasos (inc23) ──────────────────────── -->
    <section class="space-y-4">
      <div class="flex items-start justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
        <div>
          <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Saltos condicionales entre pasos</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Si un campo cumple una condición, salta a otro paso (omitiendo los intermedios).</p>
        </div>
        <button type="button" (click)="addRoutingRule()"
          class="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Añadir regla
        </button>
      </div>

      @if (routingRules.length === 0) {
        <p class="text-xs text-gray-400 dark:text-gray-500 italic">Sin reglas: los pasos avanzan de forma secuencial (1 → 2 → 3…).</p>
      }

      @for (rule of routingRules; track $index) {
        <div class="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-2 bg-gray-50/60 dark:bg-gray-900/40">
          <div class="flex flex-wrap items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <span>En el paso</span>
            <select [(ngModel)]="rule.fromStep" [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onRuleFromStepChange(rule)"
              class="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              @for (n of stepNumbers; track n) { <option [ngValue]="n">Paso {{ n }}</option> }
            </select>
            <span>si el campo</span>
            <select [(ngModel)]="rule.fieldKey" [ngModelOptions]="{ standalone: true }"
              class="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white max-w-[10rem]">
              @for (f of fieldsForStep(rule.fromStep); track f.fieldKey) { <option [ngValue]="f.fieldKey">{{ f.label }}</option> }
              @if (fieldsForStep(rule.fromStep).length === 0) { <option [ngValue]="''" disabled>Sin campos en este paso</option> }
            </select>
            <select [(ngModel)]="rule.operator" [ngModelOptions]="{ standalone: true }"
              class="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              @for (op of routeOperators; track op.value) { <option [ngValue]="op.value">{{ op.label }}</option> }
            </select>
            @if (routeNeedsValue(rule.operator)) {
              <input type="text" [(ngModel)]="rule.value" [ngModelOptions]="{ standalone: true }" placeholder="valor"
                class="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-24" />
            }
            <span>ir al</span>
            <select [(ngModel)]="rule.goToStep" [ngModelOptions]="{ standalone: true }"
              class="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              @for (n of stepNumbers; track n) { <option [ngValue]="n">Paso {{ n }}</option> }
            </select>
            <button type="button" (click)="removeRoutingRule($index)"
              class="ml-auto inline-flex items-center justify-center w-6 h-6 rounded text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors" title="Eliminar regla">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      }
    </section>
  }

  <!-- ── Tras el envío ──────────────────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      Tras el envío
    </h2>

    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Acción</label>
      <select formControlName="postSubmitAction" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500">
        @for (opt of postSubmitActions; track opt.value) {
          <option [ngValue]="opt.value">{{ opt.label }}</option>
        }
      </select>
    </div>

    @if (form.value.postSubmitAction !== 'REDIRECT') {
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Mensaje (visitantes nuevos)</label>
        <textarea formControlName="postSubmitMessage" rows="2" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"></textarea>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Mensaje (contactos conocidos)</label>
        <textarea formControlName="postSubmitMessageKnown" rows="2" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"></textarea>
      </div>
    }

    @if (form.value.postSubmitAction !== 'MESSAGE') {
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">URL de redirección</label>
        <input type="url" formControlName="postSubmitRedirectUrl" placeholder="https://..." class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Demora antes de redirigir (s)</label>
        <input type="number" formControlName="postSubmitRedirectDelayS" min="0" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
    }
  </section>

  <!-- ── Email de confirmación ──────────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      Email de confirmación
    </h2>

    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-sm text-gray-700 dark:text-gray-300">Enviar email al contacto</span>
      <input type="checkbox" formControlName="confirmEmailEnabled" class="w-4 h-4 cursor-pointer accent-brand-500">
    </label>

    @if (form.value.confirmEmailEnabled) {
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Asunto</label>
        <input type="text" formControlName="confirmEmailSubject" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Cuerpo (HTML permitido)</label>
        <textarea formControlName="confirmEmailBody" rows="4" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"></textarea>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">URL del adjunto (opcional)</label>
        <input type="url" formControlName="confirmEmailAttachmentUrl" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
    }

    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="text-sm text-gray-400 dark:text-gray-600">Doble opt-in</span>
        <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 uppercase tracking-wide">
          Próximamente
        </span>
      </div>
      <input type="checkbox" disabled class="w-4 h-4 cursor-not-allowed opacity-40 accent-brand-500">
    </div>
  </section>

  <!-- ── Notificaciones al equipo ───────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      Notificaciones al equipo
    </h2>

    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-sm text-gray-700 dark:text-gray-300">Notificar al equipo en cada envío</span>
      <input type="checkbox" formControlName="notifyTeamEnabled" class="w-4 h-4 cursor-pointer accent-brand-500">
    </label>

    @if (form.value.notifyTeamEnabled) {
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Emails separados por coma</label>
        <input type="text" formControlName="notifyTeamEmails" placeholder="ventas@empresa.com, marketing@empresa.com" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
    }
  </section>

  <!-- ── Disparadores ───────────────────────────────────────────────────── -->
  <section class="space-y-4">
    <div class="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-2">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Disparadores</h2>
      <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 uppercase tracking-wide">
        Próximamente
      </span>
    </div>

    <div class="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40 p-4 space-y-3">
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Los disparadores controlan cuándo y cómo se muestra el formulario en páginas externas. Requieren el script de incrustación.
      </p>
      <ul class="space-y-2">
        @for (item of [
          'Demora tras carga de página',
          'Scroll al % de la página',
          'Exit intent (cursor sale de la ventana)',
          'Selector CSS para disparar al clic',
          'Frecuencia de despliegue',
          'Países permitidos'
        ]; track item) {
          <li class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            <span class="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0"></span>
            {{ item }}
          </li>
        }
      </ul>
    </div>
  </section>

  <!-- ── Privacidad / GDPR ─────────────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      Privacidad / GDPR
    </h2>

    <p class="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
      El texto del checkbox se configura en la etiqueta del campo <strong class="font-semibold">Consentimiento GDPR</strong> desde el panel de propiedades del campo.
    </p>

    <!-- Opción A: URL externa -->
    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
        Opción A — URL de página de privacidad <span class="text-gray-400 font-normal">(enlace externo)</span>
      </label>
      <input type="url" formControlName="gdprPrivacyUrl" placeholder="https://empresa.com/privacidad"
        class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      @if (form.value.gdprPolicyText?.trim()) {
        <p class="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <span>⚠</span> Ignorada: hay texto de política escrito en la Opción B.
        </p>
      }
    </div>

    <!-- Separador -->
    <div class="flex items-center gap-3">
      <div class="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
      <span class="text-xs text-gray-400 dark:text-gray-500 font-medium">o</span>
      <div class="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
    </div>

    <!-- Opción B: texto inline (modal) -->
    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
        Opción B — Escribe tu política directamente <span class="text-gray-400 font-normal">(se abre en modal)</span>
      </label>
      <textarea formControlName="gdprPolicyText" rows="6"
        placeholder="Escribe aquí el texto completo de tu política de privacidad..."
        class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"></textarea>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
        Si hay texto escrito, el enlace abrirá un modal con este contenido. El texto tiene prioridad sobre la URL.
      </p>
    </div>

    <!-- Texto del enlace (aplica a ambas opciones) -->
    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Texto del enlace</label>
      <input type="text" formControlName="gdprLinkText" placeholder="Política de privacidad"
        class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Nombre del enlace que aparece junto al checkbox de consentimiento.</p>
    </div>
  </section>

  <!-- ── Retención ──────────────────────────────────────────────────────── -->
  <section class="space-y-4">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
      Retención de datos
    </h2>

    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Meses antes de purgar envíos procesados</label>
      <input type="number" formControlName="retentionMonths" min="1" class="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">El job mensual purga el día 1 a las 03:00 UTC.</p>
    </div>
  </section>

  <div class="flex justify-end pt-4 sticky bottom-0 bg-gray-50 dark:bg-gray-950 -mx-6 px-6 py-4 border-t border-gray-200 dark:border-gray-800">
    <button type="submit" [disabled]="saving"
      class="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
      @if (saving) { Guardando... } @else { Guardar configuración }
    </button>
  </div>
</form>
